// Shared print builders for the BULLETIN SCOLAIRE and the competence
// CARNET. Used by the single-student modals AND the batch printer in
// ReportsView (one print job, one page per student).
// The school logo is loaded from /school-logo.png (frontend public/)
// and hides itself if the file isn't there yet.

const TD = 'border:1px solid #444;padding:3px 5px;';
const MOY = 'background:#e8eef7;font-weight:bold;';
const GEN = 'background:#d5e2f3;font-weight:bold;';
const esc = (t) => String(t ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n/g, '<br/>');
const fmt = (v) => (v === null || v === undefined) ? null : (Math.round(v * 100) / 100);

const logoImg = () =>
  `<img src="${window.location.origin}/school-logo.png" alt="" onerror="this.style.display='none'"
        style="height:52px;margin-right:12px;"/>`;

// Header shared by both documents: logo top-left, school name, student
// identity, and the ANNÉE/CYCLE/CLASSE box on the right.
const headerHTML = (stu, cycle, year, subtitle) => `
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px;">
    <div style="display:flex;align-items:flex-start;">
      ${logoImg()}
      <div style="font-size:10px;">
        ${subtitle ? `<p style="font-weight:bold;margin:0 0 6px 0;">${subtitle}</p>` : ''}
        <table style="border-collapse:collapse;font-size:10px;margin-top:8px;">
          <tr><td style="padding:1px 6px 1px 0;font-weight:bold;">NOM ET PRÉNOM</td>
              <td style="border-bottom:1px solid #444;min-width:220px;">${esc(stu.LastName)} ${esc(stu.FirstName)}</td></tr>
          <tr><td style="padding:1px 6px 1px 0;font-weight:bold;">N°</td>
              <td style="border-bottom:1px solid #444;">${esc(stu.StudentID)}</td></tr>
        </table>
      </div>
    </div>
    <table style="border-collapse:collapse;font-size:10px;">
      <tr><td colspan="2" style="${TD}text-align:center;font-weight:bold;background:#efefef;">BULLETIN SCOLAIRE</td></tr>
      <tr><td style="${TD}font-weight:bold;">ANNÉE</td><td style="${TD}">${esc(year)}</td></tr>
      <tr><td style="${TD}font-weight:bold;">CYCLE</td><td style="${TD}">${esc(cycle || '')}</td></tr>
      <tr><td style="${TD}font-weight:bold;">CLASSE</td><td style="${TD}">${esc(stu.Class)} ${esc(stu.Section || '')}</td></tr>
    </table>
  </div>`;

const remarksHTML = (periods, remarks) => {
  const finalRow = remarks.find(r => !r.PeriodID);
  const remarkLines = periods
    .map(p => ({ p, r: remarks.find(x => x.PeriodID === p.PeriodID) }))
    .filter(x => x.r?.Remark)
    .map(x => `<p style="margin:2px 0;"><b>${esc(x.p.PeriodName)}:</b> ${esc(x.r.Remark)}</p>`)
    .join('');
  return `
    <div style="margin-top:10px;font-size:10px;">
      <p style="margin:0 0 3px 0;font-weight:bold;text-decoration:underline;">Appréciations générales</p>
      ${remarkLines || '<p style="margin:2px 0;color:#999;">&nbsp;</p>'}
      ${finalRow?.Remark ? `<p style="margin:2px 0;"><b>Fin d'année:</b> ${esc(finalRow.Remark)}</p>` : ''}
      <p style="margin:8px 0 0 0;"><b>Décision de fin d'année:</b> ${esc(finalRow?.Decision || '')}</p>
    </div>`;
};

// ============================================================
// BULLETIN SCOLAIRE
// ============================================================
export const buildBulletinHTML = (data, year) => {
  if (!data) return '';
  const { student: stu, cycle, absences, periods, exams, subjects, generalAverages, remarks = [] } = data;

  // ----- column model -----
  const groups = periods.map(p => {
    const pex = exams.filter(e => e.PeriodID === p.PeriodID);
    return { period: p, exams: pex, direct: pex.length === 1 && !!pex[0].IsDirect };
  });
  const cols = [];
  for (const g of groups) {
    if (g.direct) cols.push({ type: 'period', period: g.period });
    else {
      for (const e of g.exams) cols.push({ type: 'exam', exam: e, period: g.period });
      cols.push({ type: 'period', period: g.period, isMoy: true });
    }
  }
  cols.push({ type: 'general' });

  const valueOf = (row, c) =>
    c.type === 'exam' ? row.perExam[c.exam.ExamID]
    : c.type === 'period' ? (row.perPeriod[c.period.PeriodID] ?? undefined)
    : (row.general ?? undefined);
  // Dispensé: an exempt cell prints 'D' instead of staying blank
  const exemptOf = (row, c) =>
    c.type === 'exam' ? !!row.exemptExams?.[c.exam.ExamID]
    : c.type === 'period' ? !!row.perPeriodExempt?.[c.period.PeriodID]
    : !!row.generalExempt;
  const colStyle = (c) => c.type === 'general' ? GEN : (c.type === 'period' ? MOY : '');

  const tops = subjects; // already top-level, ordered, MaxGrade > 0

  // per-column moyenne générale /20 (coef-weighted, graded subjects only)
  const colMoyenne = (c) => {
    if (c.type === 'period') return generalAverages[c.period.PeriodID] ?? null;
    if (c.type === 'general') return generalAverages.overall ?? null;
    let coefSum = 0, acc = 0;
    for (const s of tops) {
      const v = valueOf(s, c);
      if (v !== undefined && v !== null) { coefSum += Number(s.Coefficient); acc += (v / Number(s.MaxGrade)) * 20 * Number(s.Coefficient); }
    }
    return coefSum > 0 ? Math.round((acc / coefSum) * 100) / 100 : null;
  };
  const colTotal = (c) => {
    let any = false, sum = 0;
    for (const s of tops) {
      const v = valueOf(s, c);
      if (v !== undefined && v !== null) { any = true; sum += Number(v); }
    }
    return any ? Math.round(sum * 100) / 100 : null;
  };

  // ----- header (two rows: period groups, then columns) -----
  const head1 = groups.map(g =>
    `<td style="${TD}text-align:center;font-weight:bold;background:#efefef;" colspan="${g.direct ? 1 : g.exams.length + 1}">
       ${esc(g.period.PeriodName)}<br/><span style="font-weight:normal;color:#555;font-size:8px;">${g.period.WeightPct}%</span></td>`).join('');
  const head2 = cols.map(c => {
    if (c.type === 'exam') return `<td style="${TD}text-align:center;background:#fafafa;">${esc(c.exam.ExamName)}<br/><span style="color:#777;font-size:8px;">${c.exam.WeightPct}%</span></td>`;
    if (c.type === 'period') return `<td style="${TD}text-align:center;${MOY}">${c.isMoy ? 'MOY' : 'Note'}</td>`;
    return '';
  }).join('');

  // ----- subject + domain rows -----
  const scoreTds = (row, small) => cols.map(c => {
    const v = valueOf(row, c);
    const red = v !== undefined && v !== null && Number(v) < Number(row.MaxGrade) / 2 ? 'color:#c00;' : '';
    const content = (v === undefined || v === null) ? (exemptOf(row, c) ? 'D' : '') : fmt(v);
    return `<td style="${TD}text-align:center;${colStyle(c)}${small ? 'font-size:8px;' : ''}${red}">${content}</td>`;
  }).join('');

  const bodyRows = tops.map(s => `
    <tr>
      <td style="${TD}${(s.domains || []).length ? 'font-weight:bold;' : ''}">${esc(s.SubjectNameFrench || s.SubjectName)}</td>
      <td style="${TD}text-align:center;">${fmt(Number(s.MaxGrade))}</td>
      ${scoreTds(s, false)}
    </tr>
    ${(s.domains || []).map(d => `
    <tr style="font-size:8px;color:#333;">
      <td style="${TD}padding-left:16px;">${esc(d.SubjectNameFrench || d.SubjectName)}</td>
      <td style="${TD}text-align:center;">${fmt(Number(d.MaxGrade))}</td>
      ${scoreTds(d, true)}
    </tr>`).join('')}`).join('');

  // ----- footer rows -----
  const totalMax = tops.reduce((t, s) => t + Number(s.MaxGrade), 0);
  const totalRow = `<tr>
      <td style="${TD}font-weight:bold;">TOTAL</td>
      <td style="${TD}text-align:center;font-weight:bold;">${fmt(totalMax)}</td>
      ${cols.map(c => `<td style="${TD}text-align:center;font-weight:bold;${colStyle(c)}">${fmt(colTotal(c)) || ''}</td>`).join('')}
    </tr>`;
  const moyRow = `<tr>
      <td style="${TD}font-weight:bold;">MOYENNE GÉNÉRALE</td>
      <td style="${TD}text-align:center;font-weight:bold;">20</td>
      ${cols.map(c => {
        const m = colMoyenne(c);
        return `<td style="${TD}text-align:center;font-weight:bold;${colStyle(c)}${m !== null && m < 10 ? 'color:#c00;' : ''}">${m ?? ''}</td>`;
      }).join('')}
    </tr>`;
  const resRow = `<tr>
      <td style="${TD}">RÉSULTAT (Réussi/Échoué)</td>
      <td style="${TD}"></td>
      ${cols.map(c => {
        const m = colMoyenne(c);
        return `<td style="${TD}text-align:center;font-weight:bold;${colStyle(c)}">${m === null ? '' : (m >= 10 ? 'R' : 'E')}</td>`;
      }).join('')}
    </tr>`;

  const today = new Date().toLocaleDateString('fr-FR');
  return `
    ${headerHTML(stu, cycle, year, '')}

    <table style="width:100%;border-collapse:collapse;font-size:9px;">
      <tr>
        <td style="${TD}font-weight:bold;background:#efefef;" rowspan="2">MATIÈRES</td>
        <td style="${TD}font-weight:bold;background:#efefef;text-align:center;" rowspan="2">Sur</td>
        ${head1}
        <td style="${TD}text-align:center;${GEN}" rowspan="2">MOY.<br/>GÉN.</td>
      </tr>
      <tr>${head2}</tr>
      ${bodyRows}
      ${totalRow}
      ${moyRow}
      ${resRow}
    </table>

    <p style="margin:8px 0 0 0;font-size:10px;"><b>JOURS D'ABSENCE:</b> ${absences ?? 0}</p>
    ${remarksHTML(periods, remarks)}

    <div style="display:flex;justify-content:space-between;margin-top:24px;font-size:10px;text-align:center;">
      <span>PARENTS<br/><br/>......................</span>
      <span>RESPONSABLE DE CYCLE<br/><br/>......................</span>
      <span>SIGNATURE DU CHEF D'ÉTABLISSEMENT<br/><br/>......................</span>
    </div>
    <p style="text-align:right;font-size:10px;margin-top:10px;">${today}</p>`;
};

// ============================================================
// Competence CARNET (Suivi des acquis)
// ============================================================
export const buildCarnetHTML = (data, year) => {
  if (!data) return '';
  const { student: stu, cycle, periods, levels, rows, remarks = [] } = data;
  const CTD = 'border:1px solid #555;padding:2px 4px;';

  if (!rows.length) return '<p style="text-align:center;color:#777;padding:30px;">Aucun objectif d\'apprentissage n\'est défini pour cette classe.</p>';

  // Display columns: classes with 4 direct marks (CM1/CM2/6ème) group
  // their periods two by two into SEMESTERS on the carnet — teachers
  // mark competencies on the exam period of each pair, so a group
  // takes its checkmark from whichever period of the pair carries it,
  // and its note is the pair's average. Other classes: one column
  // per period, as-is.
  const displayGroups = periods.length === 4
    ? [{ key: 'S1', label: 'Semestre 1', periods: [periods[0], periods[1]] },
       { key: 'S2', label: 'Semestre 2', periods: [periods[2], periods[3]] }]
    : periods.map(p => ({ key: String(p.PeriodID), label: p.PeriodName, periods: [p] }));

  const noteOf = (row, dg) => {
    if (!row) return null;
    const vals = dg.periods.map(p => row.perPeriod[p.PeriodID]).filter(v => v !== null && v !== undefined);
    if (!vals.length) return null;
    return Math.round((vals.reduce((s, v) => s + Number(v), 0) / vals.length) * 100) / 100;
  };
  const levelOf = (obj, dg) => {
    for (let i = dg.periods.length - 1; i >= 0; i--) {
      const lv = obj.levels[dg.periods[i].PeriodID];
      if (lv) return lv;
    }
    return null;
  };

  // Notes sub-column per group only when something is actually graded
  const flat = rows.flatMap(r => [r, ...r.domains]);
  const hasNote = {};
  for (const dg of displayGroups) hasNote[dg.key] = flat.some(r => noteOf(r, dg) !== null);
  const periodSpan = (dg) => levels.length + (hasNote[dg.key] ? 1 : 0);

  // ----- header rows -----
  const head1 = displayGroups.map(dg =>
    `<td style="${CTD}text-align:center;font-weight:bold;background:#efefef;" colspan="${periodSpan(dg)}">${esc(dg.label)}</td>`).join('');
  const head2 = displayGroups.map(dg =>
    levels.map((l, i) => `<td style="${CTD}text-align:center;background:#fafafa;width:16px;">${i + 1}</td>`).join('')
    + (hasNote[dg.key] ? `<td style="${CTD}text-align:center;background:#e8eef7;font-weight:bold;">Note</td>` : '')).join('');

  // one objective line: level checkmarks; the group's note is ONE
  // merged cell spanning all of the domain's objective rows
  const markCells = (obj, g, idx) => displayGroups.map(dg => {
    const lv = levelOf(obj, dg);
    const cells = levels.map(l =>
      `<td style="${CTD}text-align:center;">${lv === l.Value ? '✓' : ''}</td>`).join('');
    let note = '';
    if (hasNote[dg.key] && idx === 0) {
      const v = noteOf(g.noteRow, dg);
      note = `<td style="${CTD}text-align:center;vertical-align:middle;background:#eef3fa;font-weight:bold;white-space:nowrap;" rowspan="${g.objectives.length}">${v !== null && g.noteRow ? `${v} / ${fmt(g.noteRow.MaxGrade)}` : ''}</td>`;
    }
    return cells + note;
  }).join('');

  // group a row's objectives: graded-domain groups keep the domain row
  // as note carrier; DomainLabel groups label ungraded carnet domains
  const bodyParts = [];
  const totalCols = 2 + displayGroups.reduce((s, dg) => s + periodSpan(dg), 0);
  for (const subj of rows) {
    bodyParts.push(`<tr><td style="${CTD}font-weight:bold;background:#f0f0f0;" colspan="${totalCols}">
      ${esc(subj.SubjectName)}${subj.MaxGrade > 0 ? ` <span style="font-weight:normal;color:#666;">/ ${fmt(subj.MaxGrade)}</span>` : ''}</td></tr>`);

    // direct objectives, grouped by their DomainLabel (null = no domain)
    const groups = [];
    let cur = null;
    for (const o of subj.competencies) {
      if (!cur || cur.label !== (o.domainLabel || '')) { cur = { label: o.domainLabel || '', objectives: [], noteRow: o.domainLabel ? null : subj }; groups.push(cur); }
      cur.objectives.push(o);
    }
    // graded domains follow, each its own group carrying its note
    for (const d of subj.domains) {
      if (d.competencies.length) groups.push({ label: d.SubjectName, objectives: d.competencies, noteRow: d });
    }

    for (const g of groups) {
      g.objectives.forEach((o, i) => {
        bodyParts.push(`<tr style="font-size:8px;">
          ${i === 0 ? `<td style="${CTD}font-weight:bold;width:110px;" rowspan="${g.objectives.length}">${esc(g.label)}</td>` : ''}
          <td style="${CTD}">${esc(o.text)}</td>
          ${markCells(o, g, i)}
        </tr>`);
      });
    }
  }

  return `
    ${headerHTML(stu, cycle, year, "Suivi des acquis scolaires de l'élève")}

    <p style="font-size:9px;margin:0 0 4px 0;"><b>Objectifs d'apprentissage:</b>
      ${levels.map((l, i) => `&nbsp;&nbsp;<b>${i + 1}.</b> ${esc(l.Value)}`).join(' ')}</p>

    <table style="width:100%;border-collapse:collapse;font-size:8px;">
      <tr>
        <td style="${CTD}font-weight:bold;background:#efefef;" rowspan="2">Domaine</td>
        <td style="${CTD}font-weight:bold;background:#efefef;" rowspan="2">Objectifs d'apprentissage</td>
        ${head1}
      </tr>
      <tr>${head2}</tr>
      ${bodyParts.join('')}
    </table>

    ${remarksHTML(periods, remarks)}

    <div style="display:flex;justify-content:space-between;margin-top:22px;font-size:10px;text-align:center;">
      <span>Cachet de la direction<br/><br/>......................</span>
      <span>Signature des parents<br/><br/>......................</span>
    </div>`;
};

// ============================================================
// Generic printable LIST report (analytics, class lists):
// logo header + title, optional stat chips, one table.
// columns: [{ key, label, align? }] · rows: array of objects
// ============================================================
export const buildTableReportHTML = ({ title, subtitle = '', stats = [], columns, rows }) => `
  <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
    <div style="display:flex;align-items:center;">
      ${logoImg()}
      <div>
        <p style="font-weight:bold;font-size:14px;margin:0;">${esc(title)}</p>
        ${subtitle ? `<p style="font-size:10px;color:#444;margin:2px 0 0 0;">${esc(subtitle)}</p>` : ''}
      </div>
    </div>
    <span style="font-size:10px;color:#555;">${new Date().toLocaleDateString('fr-FR')}</span>
  </div>
  ${stats.length ? `<div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:8px;">
    ${stats.map(s => `<span style="border:1px solid #bbb;border-radius:4px;padding:3px 8px;font-size:10px;">
      <b>${esc(s.label)}:</b> ${esc(s.value)}</span>`).join('')}
  </div>` : ''}
  <table style="width:100%;border-collapse:collapse;font-size:10px;">
    <tr>${columns.map(c => `<td style="${TD}font-weight:bold;background:#efefef;${c.align ? `text-align:${c.align};` : ''}">${esc(c.label)}</td>`).join('')}</tr>
    ${rows.map(r => `<tr>${columns.map(c => `<td style="${TD}${c.align ? `text-align:${c.align};` : ''}">${esc(r[c.key] ?? '')}</td>`).join('')}</tr>`).join('')}
  </table>`;

// One print job, one page per student
export const printPages = (title, pages, orientation = 'portrait') => {
  const body = pages
    .map((p, i) => `<div style="${i < pages.length - 1 ? 'page-break-after:always;' : ''}">${p}</div>`)
    .join('');
  const w = window.open('', '', 'width=850,height=1000');
  if (!w) { window.alert('Popup blocked — allow popups for this site to print.'); return; }
  w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8">
    <title>${title}</title>
    <style>@page{size:A4 ${orientation};margin:1cm}*{margin:0;padding:0;box-sizing:border-box}
    body{font-family:Arial,sans-serif;color:#111;padding:2px}
    tr{page-break-inside:avoid}</style>
    </head><body>${body}</body></html>`);
  w.document.close(); w.focus();
  setTimeout(() => w.print(), 350);
};
